import React, {PropsWithChildren, useEffect, useMemo} from "react";
import {useScopedCollection} from "./useScopedCollection";
import {
    collection, doc,
    DocumentData, getDocs,
    limit,
    query,
    runTransaction,
    where,
} from 'firebase/firestore';
import {appFirestore} from "../index";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {setEmployeesWorkhours} from "../features/AppDataSlice";
import {
    selectCalendarFirstDate,
} from "../features/AppStateSlice";
import {RREmployeesStatisticsType, RREmployeeStatisticType} from "../app/types/RREmployeeStatisticType";
import {RRCalendarDataType} from "../app/types/RRCalendarDataType";
import {dateToString, showErrorsListInToast, stringToDate} from "../app/tools";

export const employeeStatisticsDataConverter = (value: DocumentData): RREmployeeStatisticType => {
    return {
        id: value.id,
        ref: value.ref,
        employeeId: value.get("employeeId"),
        monthYear: value.get("monthYear"),
        workHours: value.get("workHours") || 0,
    } as RREmployeeStatisticType
}
export const employeesStatisticsDataConverter = (data: DocumentData[]): RREmployeesStatisticsType => {
    return data.map(value => employeeStatisticsDataConverter(value));
}

export async function calculateWorkhours(date: Date, employees: string[]) {
    // Start a transaction
    try {
        await runTransaction(appFirestore, async (transaction) => {
                // For each employee in the list find the first calendar item for the month and then just update it
            for (const employeeId of employees) {
                    const firstDate = new Date(date.getFullYear(), date.getMonth(), 1);
                    const lastDate = new Date(date.getFullYear(), date.getMonth() + 1, 1);
                    const firstCalendarItem = await getDocs(
                        query(collection(appFirestore, 'calendar'),
                            where("eventDayStart", ">=", firstDate),
                            where("eventDayStart", "<", lastDate),
                            where("employeeId", "==", employeeId),
                            limit(1)
                        )
                    );
                    if (firstCalendarItem.empty) {
                        continue;
                    }
                    for (const calendarItem of firstCalendarItem.docs) {
                        console.log(employeeId);
                        console.log(calendarItem.data());
                        transaction.update(calendarItem.ref, { recalculateWorkhours: new Date() });
                    }
                }
            }
        );
    } catch (e) {
        console.error("Transaction failed: ", e);
        showErrorsListInToast(
            "Грешка", ["Неуспешно изчисляване на работни часове."]
        );
    }
}

export const FirebaseEmployeeStatisticsLoader: React.FC<PropsWithChildren<{}>> = (props) => {
    const dispatch = useAppDispatch();
    const firstDate = useAppSelector(selectCalendarFirstDate);

    const monthYear = useMemo(() => {
        const month = firstDate.getMonth();
        const year = firstDate.getFullYear();
        return month.toString() + year.toString();
    }, [firstDate]);
    const statisticsQuery = useMemo(
        () => query(collection(appFirestore, 'employeesStatistics'), where("monthYear", "==", monthYear)),
        [monthYear]
    );
    const {data, loading, error, subscription} = useScopedCollection(statisticsQuery, monthYear);

    if (error) {
        console.error(error.message);
    }

    useEffect(() => {
        if (data) {
            const statistics = new Map<string, RREmployeeStatisticType>();
            const bestByEmployee = new Map<string, {
                value: RREmployeeStatisticType;
                itemsCount: number;
                updateTime: number;
            }>();
            const _data = employeesStatisticsDataConverter(data.docs);

            _data.forEach((value, index) => {
                const sourceDoc = data.docs[index];
                const items = sourceDoc.get("items");
                const itemsCount = Array.isArray(items) ? items.length : 0;
                const updatedAt = sourceDoc.get("updatedAt");
                const updateTime = updatedAt?.toMillis?.() ?? 0;
                const existing = bestByEmployee.get(value.employeeId);

                if (!existing) {
                    bestByEmployee.set(value.employeeId, {
                        value,
                        itemsCount,
                        updateTime,
                    });
                    return;
                }

                const shouldReplace =
                    itemsCount > existing.itemsCount ||
                    (itemsCount === existing.itemsCount && value.workHours > existing.value.workHours) ||
                    (itemsCount === existing.itemsCount &&
                        value.workHours === existing.value.workHours &&
                        updateTime > existing.updateTime);

                if (shouldReplace) {
                    bestByEmployee.set(value.employeeId, {
                        value,
                        itemsCount,
                        updateTime,
                    });
                }
            });

            bestByEmployee.forEach((entry, employeeId) => {
                statistics.set(employeeId, entry.value);
            });

            dispatch(setEmployeesWorkhours(statistics));
        }
    }, [data, loading, error]);

    return <>{subscription}{props.children}</>;
}
